// src/hooks/useCapsuleCluster.js
import { useState, useCallback, useRef, useEffect } from "react";
import { fetchUnCoverTimeCapsules } from "../services/timeCapsuleService";
import { auth, db, functions as firebaseFunctions } from "../firebase";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { distanceBetween, geohashQueryBounds } from "geofire-common";
import useUserProfileStore, {
    selectUserSubscriptionData,
    selectUserSubscriptionTier,
} from "../store/useUserProfileStore";
import IconTimeCapsuleLockedMarker from "../assets/icons/v2/time-capsule-locked.webp";
import IconTimeCapsuleUnlockedMarker from "../assets/icons/v2/time-capsule-opened.webp";
import IconTimeCapsuleUnOpened from "../assets/icons/v2/time-capsule-closed.webp";
import IconTreasureClosedMarker from "../assets/icons/v2/treasure-box-locked.webp";
import IconTreasureOpenedMarker from "../assets/icons/v2/treasure-box-unlocked.webp";
import IconMarkerPublic from "../assets/icons/v2/map-pin-public.webp";
import IconSeededPublicCapsule from "../assets/icons/v2/map-pin-seeded.webp";
import IconTimeCapsuleMemory from "../assets/icons/v2/time-capsule-memory.webp";
import IconSentCapsule from "../assets/icons/v2/time-capsule-sent.webp";
import IconInsiderStar from "../assets/icons/v2/insider-star.webp";
import {
    getSponsoredBusinessAvatarMap,
    resolveSponsoredAvatar,
} from "../services/sponsoredAvatarService";
import useAppConfigFlags from "./useAppConfigFlags";
import { filterLegacyFindCapsules } from "../utils/legacyFindResults";
import { hasInsiderAccess } from "../utils/insiderAccess";
import { resolveCapsuleThumbnailMedia } from "../utils/timeCapsuleThumbnail";
const debugSponsoredAvatar =
    String(process.env.REACT_APP_DEBUG_SPONSORED_AVATAR || "").toLowerCase() === "true";
const ALL_SEED_TYPES = ["see", "eat", "drink", "do", "shop", "relax"];
const UNCOVER_RADIUS_METERS = 15000;
const EARTH_RADIUS_METERS = 6371000;

function toDateValue(input) {
    if (!input) return null;
    if (typeof input?.toDate === "function") {
        const date = input.toDate();
        return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
    }
    if (typeof input?.toMillis === "function") {
        const ms = Number(input.toMillis() || 0);
        if (!Number.isFinite(ms) || ms <= 0) return null;
        const date = new Date(ms);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(input);
    return Number.isNaN(date.getTime()) ? null : date;
}

function isExpiredUncoverCapsule(capsule) {
    const type = String(capsule?.capsuleType || "").trim().toLowerCase();
    if (type !== "sponsored" && type !== "memberexclusive") return false;

    const endDate = toDateValue(capsule?.endDate);
    if (!endDate) return false;
    return endDate.getTime() <= Date.now();
}

function normalizeCategories(categories = []) {
    return Array.isArray(categories)
        ? Array.from(
            new Set(
                categories
                    .map((c) => String(c || "").trim().toLowerCase())
                    .filter(Boolean)
            )
        )
        : [];
}

function filterCapsulesForUncoverSelection(capsules = [], selectedSeedTypeSet = new Set()) {
    const shouldFilterPublicBySeedType =
        selectedSeedTypeSet.size > 0 &&
        selectedSeedTypeSet.size < ALL_SEED_TYPES.length;

    return capsules.filter((capsule) => {
        if (isExpiredUncoverCapsule(capsule)) return false;
        if (!shouldFilterPublicBySeedType) return true;
        const type = String(capsule?.capsuleType || "").trim().toLowerCase();
        if (type === "private" || type === "sponsored") return true;
        if (type !== "public") return true;
        const seedType = String(capsule?.seedType || "").trim().toLowerCase();
        return Boolean(seedType) && selectedSeedTypeSet.has(seedType);
    });
}

function isSelfMemoryCapsule(capsule, userId) {
    const isCurrentUserSender =
        Boolean(userId) && String(capsule?.senderId || "") === String(userId);
    if (!isCurrentUserSender) return false;

    const recipientUserIds = Array.isArray(capsule?.recipientUserIds)
        ? capsule.recipientUserIds.filter(Boolean)
        : [];
    const claimedBy = Array.isArray(capsule?.claimedBy)
        ? capsule.claimedBy.filter(Boolean)
        : [];
    const recipientPhones = Array.isArray(capsule?.recipientPhoneNumbers)
        ? capsule.recipientPhoneNumbers.filter(Boolean)
        : [];

    const recipientOnlySelf =
        recipientUserIds.length > 0 &&
        userId &&
        recipientUserIds.every((id) => id === userId);
    const claimOnlySelf =
        claimedBy.length > 0 &&
        userId &&
        claimedBy.every((id) => id === userId);

    return (
        capsule?.isSelfCapsule === true ||
        capsule?.audience === "self" ||
        (userId && recipientUserIds.length === 1 && recipientUserIds[0] === userId) ||
        recipientOnlySelf ||
        (capsule?.deliveryMode === "share_claim" &&
            recipientPhones.length === 0 &&
            claimOnlySelf)
    );
}

function mapCapsulesDataToMarker(
    capsules = [],
    usersList = [],
    liveUsersById = {},
    sponsoredBusinessAvatarMap = {},
    { userId = "", isSubscribedUser = false } = {}
) {
    const now = Date.now();
    return capsules.filter((cap) => cap?.discoverOnly !== true).map((cap) => {
        const normalizedCapsuleType = String(cap?.capsuleType || "").toLowerCase();
        const resolvedBusinessId = String(cap?.businessId || cap?.senderId || "").trim();
        const sender =
            liveUsersById[cap.senderId] ||
            usersList.find((x) => x.uid === cap.senderId);
        const revealTs = cap.revealDate?.toDate?.().getTime?.() ?? -Infinity;
        const isRead = cap?.readBy?.includes(userId);
        const isSelfMemory = isSelfMemoryCapsule(cap, userId);
        const senderProfileImage =
            sender?.profileImage ||
            sender?.avatarUrl ||
            sender?.photoURL ||
            cap.senderProfileImage ||
            "";
        let iconSrc = IconMarkerPublic;

        if (cap?.insider === true) {
            iconSrc = IconInsiderStar;
        } else if (normalizedCapsuleType === "private") {
            iconSrc = isSelfMemory
                ? IconTimeCapsuleMemory
                : cap.senderId === userId
                ? IconSentCapsule
                : revealTs > now
                ? IconTimeCapsuleLockedMarker
                : isRead
                ? IconTimeCapsuleUnlockedMarker
                : IconTimeCapsuleUnOpened;
        } else if (normalizedCapsuleType === "memberexclusive") {
            iconSrc = isSubscribedUser
                ? IconTreasureOpenedMarker
                : IconTreasureClosedMarker;
        } else if (normalizedCapsuleType === "public") {
            iconSrc = cap?.seeded === true ? IconSeededPublicCapsule : IconMarkerPublic;
        } else if (normalizedCapsuleType === "sponsored") {
            const businessAvatarUrl =
                String(
                    sponsoredBusinessAvatarMap[resolvedBusinessId] || ""
                ).trim();
            const resolvedSponsoredAvatar = resolveSponsoredAvatar({
                capsuleType: cap.capsuleType,
                businessAvatarUrl,
                creatorProfileImage: sender?.profileImage || "",
                senderProfileImage: cap.senderProfileImage || "",
            });
            if (debugSponsoredAvatar) {
                console.log("[sponsored-avatar] uncover marker resolve", {
                    capsuleId: cap?.id,
                    senderId: cap?.senderId,
                    businessId: resolvedBusinessId,
                    businessAvatarUrl,
                    creatorProfileImage: sender?.profileImage || "",
                    senderProfileImage: cap?.senderProfileImage || "",
                    resolvedSponsoredAvatar,
                });
            }
            iconSrc = resolvedSponsoredAvatar || senderProfileImage || IconSeededPublicCapsule;
        }

        const size =
            cap.capsuleType === "private"
                ? { width: 80, height: 80 }
                : { width: 60, height: 60 };
        const thumbnailMedia = resolveCapsuleThumbnailMedia(cap);

        return {
            id: cap.id,
            position: { lat: cap.location.lat, lng: cap.location.lng },
            title: cap.messageTitle,
            body: cap.messageBody,
            heroUrl: thumbnailMedia.url || null,
            heroType: thumbnailMedia.type || null,
            address: cap.location.address,
            icon: iconSrc,
            revealDate: cap.revealDate,
            capsuleType: normalizedCapsuleType,
            senderId: cap.senderId,
            senderProfileImage: cap.senderProfileImage || "",
            businessId: resolvedBusinessId,
            businessAvatarUrl: cap.businessAvatarUrl || "",
            recipientUserIds: cap.recipientUserIds,
            recipientPhoneNumbers: cap.recipientPhoneNumbers,
            claimedBy: cap.claimedBy,
            deliveryMode: cap.deliveryMode,
            audience: cap.audience,
            isSelfCapsule: cap.isSelfCapsule,
            hashtags: cap.hashtags,
            callToActionText: cap.callToActionText,
            callToActionUrl: cap.callToActionUrl,
            insider: cap.insider === true,
            seeded: cap.seeded === true,
            seedType: String(cap.seedType || "").trim().toLowerCase(),
            ...size,
        };
    });
}

function distanceMeters(a, b) {
    const lat1 = Number(a?.lat);
    const lng1 = Number(a?.lng);
    const lat2 = Number(b?.lat);
    const lng2 = Number(b?.lng);
    if (
        !Number.isFinite(lat1) ||
        !Number.isFinite(lng1) ||
        !Number.isFinite(lat2) ||
        !Number.isFinite(lng2)
    ) {
        return Number.POSITIVE_INFINITY;
    }
    const toRad = (value) => (value * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const x1 = toRad(lat1);
    const x2 = toRad(lat2);
    const h =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(x1) * Math.cos(x2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return EARTH_RADIUS_METERS * c;
}

function toMs(ts) {
    if (!ts) return 0;
    if (typeof ts?.toMillis === "function") return Number(ts.toMillis() || 0);
    if (typeof ts?.toDate === "function") {
        const d = ts.toDate();
        return d instanceof Date ? Number(d.getTime() || 0) : 0;
    }
    const asDate = new Date(ts);
    return Number(asDate.getTime() || 0);
}

function dedupeCapsulesForDisplay(capsules = []) {
    const keepByKey = new Map();
    (Array.isArray(capsules) ? capsules : []).forEach((capsule) => {
        const type = String(capsule?.capsuleType || "").trim().toLowerCase();
        const placeId = String(capsule?.location?.placeId || "").trim();
        const businessId = String(capsule?.businessId || capsule?.senderId || "").trim();
        const dedupeKey =
            capsule?.seeded === true && placeId
                ? `${type}:${businessId}:${placeId}`
                : String(capsule?.id || "");
        if (!dedupeKey) return;
        const existing = keepByKey.get(dedupeKey);
        if (!existing) {
            keepByKey.set(dedupeKey, capsule);
            return;
        }
        const existingMs = Math.max(toMs(existing?.updatedAt), toMs(existing?.createdAt));
        const nextMs = Math.max(toMs(capsule?.updatedAt), toMs(capsule?.createdAt));
        if (nextMs >= existingMs) {
            keepByKey.set(dedupeKey, capsule);
        }
    });
    return Array.from(keepByKey.values());
}

function markerCoordinateKey(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "";
    return num.toFixed(6);
}

function buildMarkerComparisonKey(marker = {}) {
    const revealMs = toMs(marker?.revealDate);
    return [
        String(marker?.id || ""),
        String(marker?.capsuleType || ""),
        String(marker?.seedType || ""),
        marker?.seeded === true ? "1" : "0",
        String(marker?.icon || ""),
        String(marker?.title || ""),
        String(marker?.body || ""),
        String(marker?.address || ""),
        String(marker?.senderId || ""),
        String(marker?.businessId || ""),
        marker?.insider === true ? "1" : "0",
        String(marker?.heroUrl || ""),
        markerCoordinateKey(marker?.position?.lat),
        markerCoordinateKey(marker?.position?.lng),
        markerCoordinateKey(marker?.width),
        markerCoordinateKey(marker?.height),
        String(revealMs),
    ].join("|");
}

function areMarkersEquivalent(prevMarkers = [], nextMarkers = []) {
    if (prevMarkers === nextMarkers) return true;
    if (!Array.isArray(prevMarkers) || !Array.isArray(nextMarkers)) return false;
    if (prevMarkers.length !== nextMarkers.length) return false;
    for (let i = 0; i < prevMarkers.length; i += 1) {
        if (buildMarkerComparisonKey(prevMarkers[i]) !== buildMarkerComparisonKey(nextMarkers[i])) {
            return false;
        }
    }
    return true;
}

export default function useCapsuleCluster(mapCenter, usersList) {
    const [open, setOpen] = useState(false);
    const [markers, setMarkers] = useState([]);
    const [mode, setMode] = useState("discover");
    const subscriptionData = useUserProfileStore(selectUserSubscriptionData);
    const subscriptionTier = useUserProfileStore(selectUserSubscriptionTier);
    const { uncoverSeedingEnabled } = useAppConfigFlags();
    const liveUncoverUnsubsRef = useRef([]);
    const sponsoredAvatarCacheRef = useRef({});
    const sponsoredAvatarInFlightRef = useRef(new Set());

    const sortMarkersForDisplay = useCallback(
        (markerList = []) => {
            const center = {
                lat: Number(mapCenter?.lat),
                lng: Number(mapCenter?.lng),
            };
            const sorted = Array.isArray(markerList) ? [...markerList] : [];
            sorted.sort((a, b) => {
                const rank = (marker) => {
                    if (marker?.insider === true) return 0;
                    const type = String(marker?.capsuleType || "").trim().toLowerCase();
                    if (type === "sponsored") return 1;
                    if (type === "memberexclusive") return 2;
                    if (type === "private") return 3;
                    if (type === "public") return 4;
                    return 5;
                };
                const rankDiff = rank(a) - rank(b);
                if (rankDiff !== 0) {
                    return rankDiff;
                }

                const aDistance = distanceMeters(center, a?.position);
                const bDistance = distanceMeters(center, b?.position);
                if (aDistance !== bDistance) {
                    return aDistance - bDistance;
                }

                return String(a?.title || a?.id || "").localeCompare(
                    String(b?.title || b?.id || "")
                );
            });
            return sorted;
        },
        [mapCenter]
    );

    const setMarkersIfChanged = useCallback((nextMarkers = []) => {
        const normalized = Array.isArray(nextMarkers) ? nextMarkers : [];
        setMarkers((prevMarkers) =>
            areMarkersEquivalent(prevMarkers, normalized) ? prevMarkers : normalized
        );
    }, []);

    const stopLiveUncoverStream = useCallback(() => {
        if (!liveUncoverUnsubsRef.current.length) return;
        liveUncoverUnsubsRef.current.forEach((unsubscribe) => {
            try {
                if (typeof unsubscribe === "function") unsubscribe();
            } catch (err) {
                console.warn("Failed to stop uncover live listener:", err);
            }
        });
        liveUncoverUnsubsRef.current = [];
    }, []);

    const startLiveUncoverStream = useCallback(
        ({ categories = [], uid = "", isSubscribedUser = false } = {}) => {
            stopLiveUncoverStream();
            if (
                !mapCenter ||
                !Number.isFinite(mapCenter.lat) ||
                !Number.isFinite(mapCenter.lng)
            ) {
                return;
            }

            const normalizedCategories = normalizeCategories(categories);
            const selectedSeedTypeSet = new Set(normalizedCategories);
            const bounds = geohashQueryBounds(
                [Number(mapCenter.lat), Number(mapCenter.lng)],
                UNCOVER_RADIUS_METERS
            );
            const queryResultsByKey = new Map();

            const recompute = () => {
                const mergedById = new Map();
                queryResultsByKey.forEach((capsuleMap) => {
                    capsuleMap.forEach((capsule, id) => {
                        mergedById.set(id, capsule);
                    });
                });

                const nearbyCapsules = Array.from(mergedById.values()).filter((capsule) => {
                    const lat = Number(capsule?.location?.lat);
                    const lng = Number(capsule?.location?.lng);
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

                    const distanceMetersFromCenter = distanceBetween(
                        [Number(mapCenter.lat), Number(mapCenter.lng)],
                        [lat, lng]
                    ) * 1000;
                    if (distanceMetersFromCenter > UNCOVER_RADIUS_METERS) return false;

                    const archivedBy = Array.isArray(capsule?.archivedBy)
                        ? capsule.archivedBy
                        : [];
                    const deletedBy = Array.isArray(capsule?.deletedBy)
                        ? capsule.deletedBy
                        : [];
                    if (uid && (archivedBy.includes(uid) || deletedBy.includes(uid))) {
                        return false;
                    }
                    return true;
                });

                const filteredCapsules = filterCapsulesForUncoverSelection(
                    dedupeCapsulesForDisplay(nearbyCapsules),
                    selectedSeedTypeSet
                );
                const sponsoredBusinessIds = Array.from(
                    new Set(
                        filteredCapsules
                            .filter(
                                (capsule) =>
                                    String(capsule?.capsuleType || "").trim().toLowerCase() ===
                                    "sponsored"
                            )
                            .map((capsule) =>
                                String(capsule?.businessId || capsule?.senderId || "").trim()
                            )
                            .filter(Boolean)
                    )
                );
                const cachedSponsoredAvatarMap = sponsoredBusinessIds.reduce((acc, businessId) => {
                    acc[businessId] = String(
                        sponsoredAvatarCacheRef.current[businessId] || ""
                    ).trim();
                    return acc;
                }, {});
                const baseMarkers = mapCapsulesDataToMarker(
                    filteredCapsules,
                    usersList,
                    {},
                    cachedSponsoredAvatarMap,
                    {
                        userId: uid,
                        isSubscribedUser,
                    }
                );
                setMarkersIfChanged(sortMarkersForDisplay(baseMarkers));

                const missingBusinessIds = sponsoredBusinessIds.filter((businessId) => {
                    const hasCached = Object.prototype.hasOwnProperty.call(
                        sponsoredAvatarCacheRef.current,
                        businessId
                    );
                    if (hasCached) return false;
                    return !sponsoredAvatarInFlightRef.current.has(businessId);
                });
                if (!missingBusinessIds.length) return;

                missingBusinessIds.forEach((businessId) =>
                    sponsoredAvatarInFlightRef.current.add(businessId)
                );
                getSponsoredBusinessAvatarMap(missingBusinessIds)
                    .then((avatarMap) => {
                        missingBusinessIds.forEach((businessId) => {
                            sponsoredAvatarInFlightRef.current.delete(businessId);
                            sponsoredAvatarCacheRef.current[businessId] = String(
                                avatarMap?.[businessId] || ""
                            ).trim();
                        });
                        recompute();
                    })
                    .catch((err) => {
                        missingBusinessIds.forEach((businessId) => {
                            sponsoredAvatarInFlightRef.current.delete(businessId);
                            if (
                                !Object.prototype.hasOwnProperty.call(
                                    sponsoredAvatarCacheRef.current,
                                    businessId
                                )
                            ) {
                                sponsoredAvatarCacheRef.current[businessId] = "";
                            }
                        });
                        console.warn("Failed to hydrate sponsored avatars in uncover stream:", err);
                    });
            };

            bounds.forEach(([start, end], index) => {
                const publicQuery = query(
                    collection(db, "timeCapsules"),
                    where("geohash", ">=", start),
                    where("geohash", "<=", end),
                    where("status", "==", "published"),
                    where("capsuleType", "!=", "private")
                );
                const publicUnsub = onSnapshot(
                    publicQuery,
                    (snap) => {
                        const nextMap = new Map();
                        snap.forEach((docSnap) => {
                            nextMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
                        });
                        queryResultsByKey.set(`public-${index}`, nextMap);
                        recompute();
                    },
                    (err) => {
                        console.warn("Uncover live public listener failed:", err);
                    }
                );
                liveUncoverUnsubsRef.current.push(publicUnsub);

                if (!uid) return;

                const privateRecipientQuery = query(
                    collection(db, "timeCapsules"),
                    where("geohash", ">=", start),
                    where("geohash", "<=", end),
                    where("status", "in", ["delivered", "sent"]),
                    where("capsuleType", "==", "private"),
                    where("recipientUserIds", "array-contains", uid)
                );
                const recipientUnsub = onSnapshot(
                    privateRecipientQuery,
                    (snap) => {
                        const nextMap = new Map();
                        snap.forEach((docSnap) => {
                            nextMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
                        });
                        queryResultsByKey.set(`private-recipient-${index}`, nextMap);
                        recompute();
                    },
                    (err) => {
                        console.warn("Uncover live private recipient listener failed:", err);
                    }
                );
                liveUncoverUnsubsRef.current.push(recipientUnsub);

                const privateSenderQuery = query(
                    collection(db, "timeCapsules"),
                    where("geohash", ">=", start),
                    where("geohash", "<=", end),
                    where("status", "in", ["delivered", "sent"]),
                    where("capsuleType", "==", "private"),
                    where("senderId", "==", uid)
                );
                const senderUnsub = onSnapshot(
                    privateSenderQuery,
                    (snap) => {
                        const nextMap = new Map();
                        snap.forEach((docSnap) => {
                            nextMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
                        });
                        queryResultsByKey.set(`private-sender-${index}`, nextMap);
                        recompute();
                    },
                    (err) => {
                        console.warn("Uncover live private sender listener failed:", err);
                    }
                );
                liveUncoverUnsubsRef.current.push(senderUnsub);
            });
        },
        [mapCenter, setMarkersIfChanged, sortMarkersForDisplay, stopLiveUncoverStream, usersList]
    );

    const hasSeededNearbyForCategories = useCallback(
        async (categories = []) => {
            if (
                !mapCenter ||
                !Number.isFinite(mapCenter.lat) ||
                !Number.isFinite(mapCenter.lng)
            ) {
                return false;
            }

            const normalizedCategories = normalizeCategories(categories);
            const selectedSeedTypeSet = new Set(normalizedCategories);
            const capsules = await fetchUnCoverTimeCapsules({
                center: mapCenter,
                isUnCover: true,
            });

            return capsules.some((capsule) => {
                if (isExpiredUncoverCapsule(capsule)) return false;
                if (capsule?.seeded !== true) return false;
                const type = String(capsule?.capsuleType || "").trim().toLowerCase();
                if (type !== "public" && type !== "sponsored") return false;
                if (
                    !selectedSeedTypeSet.size ||
                    selectedSeedTypeSet.size === ALL_SEED_TYPES.length
                ) {
                    return true;
                }
                const seedType = String(capsule?.seedType || "").trim().toLowerCase();
                return Boolean(seedType) && selectedSeedTypeSet.has(seedType);
            });
        },
        [mapCenter]
    );

    const openClusterFromMap = useCallback((clusterMarkers) => {
        stopLiveUncoverStream();
        setMode("cluster");
        setMarkersIfChanged(sortMarkersForDisplay(clusterMarkers));
        setOpen(true);
    }, [setMarkersIfChanged, sortMarkersForDisplay, stopLiveUncoverStream]);

    const openDiscoverFromMarkers = useCallback((discoverMarkers = []) => {
        if (!Array.isArray(discoverMarkers)) return;
        stopLiveUncoverStream();
        if (!discoverMarkers.length) {
            setMarkersIfChanged([]);
            setOpen(false);
            return;
        }
        setMode("discover");
        setMarkersIfChanged(sortMarkersForDisplay(discoverMarkers));
        setOpen(true);
    }, [setMarkersIfChanged, sortMarkersForDisplay, stopLiveUncoverStream]);

    const openUncoverCluster = useCallback(
      async ({
        categories = [],
        skipSeeding = false,
        progressId = "",
        seedCallableName = "uncover_seedSponsoredCapsules",
        legacyFindOnly = false,
      } = {}) => {
        const uid = auth.currentUser?.uid || "";
        const isSubscribedUser = hasInsiderAccess(subscriptionData, subscriptionTier);
        let seedingError = null;
        let seedingSummary = null;

        const normalizedCategories = normalizeCategories(categories);
        if (legacyFindOnly) {
            stopLiveUncoverStream();
        }

        const initialCapsules = await fetchUnCoverTimeCapsules({
            center: mapCenter,
            isUnCover: true,
        });
        const dedupedInitialCapsules = dedupeCapsulesForDisplay(initialCapsules);
        const selectedSeedTypeSet = new Set(normalizedCategories);
        const legacyInitialCapsules = legacyFindOnly
            ? filterLegacyFindCapsules(dedupedInitialCapsules, normalizedCategories)
            : [];
        const hasSeededMatchNearby = legacyFindOnly
            ? legacyInitialCapsules.length > 0
            : dedupedInitialCapsules.some((capsule) => {
            if (isExpiredUncoverCapsule(capsule)) return false;
            if (capsule?.seeded !== true) return false;
            const type = String(capsule?.capsuleType || "").trim().toLowerCase();
            if (type !== "public" && type !== "sponsored") return false;
            if (!selectedSeedTypeSet.size || selectedSeedTypeSet.size === ALL_SEED_TYPES.length) {
                return true;
            }
            const seedType = String(capsule?.seedType || "").trim().toLowerCase();
            return Boolean(seedType) && selectedSeedTypeSet.has(seedType);
        });

        let capsules = dedupedInitialCapsules;
        const shouldSeed =
            !skipSeeding &&
            mapCenter &&
            Number.isFinite(mapCenter.lat) &&
            Number.isFinite(mapCenter.lng) &&
            normalizedCategories.length > 0 &&
            uncoverSeedingEnabled === true;

        if (hasSeededMatchNearby) {
            const filteredInitialCapsules = legacyFindOnly
                ? legacyInitialCapsules
                : filterCapsulesForUncoverSelection(
                    dedupedInitialCapsules,
                    selectedSeedTypeSet
                );
            const baseMarkers = mapCapsulesDataToMarker(
                filteredInitialCapsules,
                usersList,
                {},
                {},
                {
                    userId: uid,
                    isSubscribedUser,
                }
            );
            setMode("discover");
            setMarkersIfChanged(sortMarkersForDisplay(baseMarkers));
            setOpen(true);
            if (!legacyFindOnly) {
                startLiveUncoverStream({
                    categories: normalizedCategories,
                    uid,
                    isSubscribedUser,
                });
            }
        }

        if (
            shouldSeed &&
            !hasSeededMatchNearby
        ) {
            try {
                const seedFn = httpsCallable(
                    firebaseFunctions,
                    String(seedCallableName || "uncover_seedSponsoredCapsules")
                );
                const seeded = await seedFn({
                    center: {
                        lat: Number(mapCenter.lat),
                        lng: Number(mapCenter.lng),
                    },
                    categories: normalizedCategories,
                    progressId: String(progressId || "").trim(),
                });
                seedingSummary = seeded?.data || null;
                const nextCapsules = await fetchUnCoverTimeCapsules({
                    center: mapCenter,
                    isUnCover: true,
                });
                capsules = dedupeCapsulesForDisplay(nextCapsules);
            } catch (err) {
                seedingError = err;
                console.warn("Uncover sponsored seeding failed:", err);
            }
        } else if (shouldSeed && hasSeededMatchNearby) {
            // Keep repeated uncover at the same area stable: if seeded results already
            // exist for this selection, we open them immediately without triggering
            // another seed run from this uncover action.
            seedingSummary = {
                skipped: true,
                reason: "opened_existing_seeded_no_reseed",
            };
        } else if (
            !skipSeeding &&
            uncoverSeedingEnabled !== true &&
            mapCenter &&
            Number.isFinite(mapCenter.lat) &&
            Number.isFinite(mapCenter.lng) &&
            normalizedCategories.length > 0 &&
            !hasSeededMatchNearby
        ) {
            seedingSummary = {
                skipped: true,
                reason: "feature_disabled",
            };
        }

        const filteredCapsules = legacyFindOnly
            ? filterLegacyFindCapsules(capsules, normalizedCategories)
            : filterCapsulesForUncoverSelection(
                capsules,
                selectedSeedTypeSet
            );
        const senderIds = Array.from(
            new Set(filteredCapsules.map((c) => c.senderId).filter(Boolean))
        );

        const liveUsersById = {};
        await Promise.all(
            senderIds.map(async (senderId) => {
                try {
                    const snap = await getDoc(doc(db, "users", senderId));
                    if (!snap.exists()) return;
                    const data = snap.data() || {};
                    liveUsersById[senderId] = {
                        uid: senderId,
                        profileImage: data.profileImage || "",
                        displayName: data.displayName || "",
                    };
                } catch (err) {
                    console.warn("Failed to fetch sender profile:", senderId, err);
                }
            })
        );
        setMode("discover");
        const baseMarkers = mapCapsulesDataToMarker(filteredCapsules, usersList, liveUsersById, {}, {
                userId: uid,
                isSubscribedUser,
            });
        if (!baseMarkers.length) {
            setMarkersIfChanged([]);
            setOpen(false);
            stopLiveUncoverStream();
            return {
                skipped: true,
                reason: "no_uncover_results",
                seedingError,
                seedingSummary,
                visibleCount: 0,
            };
        }
        setMarkersIfChanged(sortMarkersForDisplay(baseMarkers));
        setOpen(true);
        if (!legacyFindOnly) {
            startLiveUncoverStream({
                categories: normalizedCategories,
                uid,
                isSubscribedUser,
            });
        }

        const sponsoredBusinessIds = filteredCapsules
            .filter((cap) => String(cap?.capsuleType || "").toLowerCase() === "sponsored")
            .map((cap) => cap?.businessId || cap?.senderId);
        if (!sponsoredBusinessIds.length) {
            return {
                seedingError,
                seedingSummary,
                visibleCount: filteredCapsules.length,
            };
        }

        getSponsoredBusinessAvatarMap(sponsoredBusinessIds)
            .then((sponsoredBusinessAvatarMap) => {
                setMarkers((prevMarkers) => {
                    let didChange = false;
                    const nextMarkers = prevMarkers.map((marker) => {
                        if (String(marker?.capsuleType || "").toLowerCase() !== "sponsored") {
                            return marker;
                        }
                        const businessAvatarUrl =
                            String(
                                sponsoredBusinessAvatarMap[String(marker?.businessId || marker?.senderId || "").trim()] ||
                                ""
                            ).trim();
                        const creatorProfileImage =
                            liveUsersById[marker?.senderId]?.profileImage ||
                            liveUsersById[marker?.senderId]?.avatarUrl ||
                            liveUsersById[marker?.senderId]?.photoURL ||
                            usersList.find((x) => x.uid === marker?.senderId)?.profileImage ||
                            usersList.find((x) => x.uid === marker?.senderId)?.avatarUrl ||
                            "";
                        const resolved = resolveSponsoredAvatar({
                            capsuleType: marker?.capsuleType,
                            businessAvatarUrl,
                            creatorProfileImage,
                            senderProfileImage: marker?.senderProfileImage || "",
                        });
                        if (!resolved || resolved === marker.icon) return marker;
                        didChange = true;
                        return { ...marker, icon: resolved };
                    });
                    return didChange ? nextMarkers : prevMarkers;
                });
            })
            .catch((err) => {
                console.warn("Failed to hydrate sponsored business avatars for uncover:", err);
            });
        return {
            seedingError,
            seedingSummary,
            visibleCount: filteredCapsules.length,
        };
      },
      [
        mapCenter,
        setMarkersIfChanged,
        sortMarkersForDisplay,
        startLiveUncoverStream,
        stopLiveUncoverStream,
        subscriptionData,
        subscriptionTier,
        uncoverSeedingEnabled,
        usersList,
      ]
    );

    const runBackgroundSeedRefresh = useCallback(
        async ({ categories = [], deviceId = "" } = {}) => {
            if (uncoverSeedingEnabled !== true) {
                return { skipped: true, reason: "feature_disabled" };
            }
            const normalizedCategories = normalizeCategories(categories);
            if (
                !mapCenter ||
                !Number.isFinite(mapCenter.lat) ||
                !Number.isFinite(mapCenter.lng)
            ) {
                return { skipped: true, reason: "missing_center" };
            }
            if (!normalizedCategories.length) {
                return { skipped: true, reason: "missing_categories" };
            }

            const hasSeededNearby = await hasSeededNearbyForCategories(normalizedCategories);
            if (!hasSeededNearby) {
                return { skipped: true, reason: "no_seeded_nearby" };
            }

            const seedFn = httpsCallable(firebaseFunctions, "uncover_seedSponsoredCapsules");
            const res = await seedFn({
                center: {
                    lat: Number(mapCenter.lat),
                    lng: Number(mapCenter.lng),
                },
                categories: normalizedCategories,
                backgroundRefresh: true,
                deviceId: String(deviceId || "").trim(),
            });
            return res?.data || { skipped: true, reason: "empty_response" };
        },
        [hasSeededNearbyForCategories, mapCenter, uncoverSeedingEnabled]
    );

    useEffect(() => {
        return () => {
            stopLiveUncoverStream();
        };
    }, [stopLiveUncoverStream]);

    const close = useCallback(() => {
        stopLiveUncoverStream();
        setOpen(false);
        setMode("discover");
    }, [stopLiveUncoverStream]);

    return {
        open,
        markers,
        mode,
        openClusterFromMap,
        openDiscoverFromMarkers,
        openUncoverCluster,
        runBackgroundSeedRefresh,
        close,
    };
}
